import { ref, onBeforeUnmount } from 'vue'
import { showToast, showLoadingToast, closeToast } from 'vant'
import { useI18n } from 'vue-i18n'
import {
  useVideoCallStore,
  VIDEO_CALL_FAILURE_REASON
} from '@/stores/videoCall'
import { useUserStore } from '@/stores/user'
import { loveRoomStart } from '@/api/video'
import { showVideoCall, closeVideoCall } from '@/components/VideoCallModal'
import { RELATION_TYPE, GENDER } from '@/common/constants/user'
import { LIVE_ROOM_COME_FROM } from '@/common/constants'
import { getVideoCostInfo, getOnlineStatusInfo } from '@/utils'
import { handleVideoCallError } from '@/utils/videoCallErrorHandler'
import { handleCreateTracksError } from '@/utils/createTracksErrorHandler'
import agoraManager from '@/managers/AgoraRTCManager'
import agoraEventBus, { AGORA_EVENTS } from '@/events/agoraEvents'
import { useRouter } from 'vue-router'
import { track } from '@/utils/tracker'

/**
 * 视频通话 Hook
 * @param {Object} options - 配置选项
 * @param {number} options.comeFrom - 来源标识，详见 LIVE_ROOM_COME_FROM
 * @param {number} options.waitingTimeout - 等待超时时间（秒，默认 30）
 * @param {Function} options.onSuccess - 对方接听回调
 * @param {Function} options.onRefused - 被拒绝回调
 * @param {Function} options.onTimeout - 超时回调
 * @param {Function} options.onFailure - 失败回调
 * @param {Function} options.onHangup - 用户挂断回调
 * @param {number} options.popId - 弹窗 ID
 * @returns {Object} - Hook 返回值
 */
export function useVideoCall(options = {}) {
  const {
    waitingTimeout = 30,
    onTimeout,
    onFailure,
    onHangup,
    comeFrom = LIVE_ROOM_COME_FROM.PRIVATE,
    popId,
  } = options

  const { t } = useI18n()
  const videoCallStore = useVideoCallStore()
  const userStore = useUserStore()
  const router = useRouter()

  const isProcessing = ref(false)

  // 保存当前通话的 traceId 和 timestamp（用于 cancelCallApi）
  let currentTraceId = null
  let currentTimestamp = null

  // 保存USER_JOINED事件监听器和通话状态
  let userJoinedHandler = null
  let isCallActive = false

  /**
   * 检查用户关系 ✅
   */
  function checkUserRelation(userRelation) {
    if (!userRelation) return true

    const { type } = userRelation

    let error, reason;
    // 检查是否被拉黑
    if (type === RELATION_TYPE.BLOCKED) {
      error = t('common.youBlockedUser')
      reason = VIDEO_CALL_FAILURE_REASON.BLOCKED
    }
    if (type === RELATION_TYPE.BE_BLOCKED) {
      error = t('common.youBeenBlocked')
      reason = VIDEO_CALL_FAILURE_REASON.BE_BLOCKED
    }
    if (error) {
      showToast(error)

      onFailure?.({
        reason,
        message: error,
      })
      return false
    }

    return true
  }

  /**
   * 取消通话（通知服务器）
   * @param {number} actionType - 取消类型
   * @param {string} traceId - 追踪 ID
   * @param {number} timestamp - 时间戳
   * @returns {Promise<void>}
   */
  async function cancelCallApi(actionType, traceId, timestamp) {
    try {
      const currentRoomInfo = videoCallStore.currentRoomInfo
      if (!currentRoomInfo) {
        console.warn('[useVideoCall] No active call to cancel')
        return
      }

      // 从 currentRoomInfo 的 live_members 中获取对方的 member_id
      const members = Object.values(currentRoomInfo.live_members || {})
      const targetMember = members.find(m => String(m.member_id) !== String(userStore.getMemberId()))

      if (!targetMember) {
        console.error('[useVideoCall] Target member not found in live_members')
        return
      }

      await loveRoomStart({
        target_id: targetMember.id,
        action: loveRoomStart.ACTION.CANCEL,
        action_type: actionType,
        mode: loveRoomStart.MODE.VIDEO,
        trace_id: traceId,
        timestamp: timestamp,
        live_id: currentRoomInfo.live_id,
        // count: 0,
        gender: GENDER.FEMALE, // 普通用户默认传女性
        come_from: comeFrom,
        // reason: '',
        // jump_type: '',
        // match_mode: '',
        pop_id: popId,
      }, {
        hideLoading: true,
      })

      console.log('[useVideoCall] Call cancelled successfully')
    } catch (error) {
      console.error('[useVideoCall] Failed to cancel call:', error)
      // 取消接口失败不影响本地清理，继续执行后续流程
    }
  }

  /**
   * 清理 Agora 资源
   */
  async function cleanupAgoraResources() {
    try {
      await agoraManager.destroy()
    } catch (error) {
      console.error('[useVideoCall] Failed to cleanup Agora resources:', error)
    }
  }

  /**
   * 清理通话相关资源（主动清理，不依赖 watch）
   * @param {Object} options - 清理选项
   * @param {boolean} options.closeToastAndDialog - 是否关闭 Toast 和弹窗（默认 true）
   */
  async function cleanupCallResources({ closeToastAndDialog = true } = {}) {
    console.log('[useVideoCall] Cleaning up call resources...')

    // 标记通话结束
    isCallActive = false

    // 清理 USER_JOINED 监听
    if (userJoinedHandler) {
      agoraEventBus.off(AGORA_EVENTS.USER_JOINED, userJoinedHandler)
      userJoinedHandler = null
    }

    // 关闭弹窗和 Toast（可选）
    if (closeToastAndDialog) {
      closeToast()
      closeVideoCall()
    }

    // 清理 Agora 资源
    await cleanupAgoraResources()

    // 重置处理状态
    isProcessing.value = false
  }

  /**
   * 上报视频通话发起埋点
   */
  function trackVideoCallInitiate({ targetMemberId, anchorStatus, actionResult, cardType }) {
    track('call_1v1_call_button_click_anchor_profile', {
      anchor_id: String(targetMemberId),
      anchor_status: getOnlineStatusInfo(anchorStatus ? anchorStatus.online_status : null).status,
      card_type: cardType
        ? (cardType === getVideoCostInfo.CARD_TYPE.VIDEO_CARD
            ? '1v1_call_card'
            : '1v1_exp_card')
        : 'none',
      action_result: actionResult,
    })
  }

  /**
   * Step 1: 创建本地轨道
   * @returns {Promise<boolean>} - 成功返回true，失败返回false
   */
  async function createLocalTracks() {
    try {
      await agoraManager.createTracks()
      return true
    } catch (error) {
      const errorResult = handleCreateTracksError(error)
      onFailure?.(errorResult)
      return false
    }
  }

  /**
   * Step 2: 调用API并加入Agora频道
   * @param {Object} params - 参数
   * @returns {Promise<Object|null>} - 成功返回roomInfo，失败返回null
   */
  async function startCallAndJoinChannel({ targetId, currentMemberId }) {
    try {
      currentTimestamp = Date.now()
      currentTraceId = `${currentMemberId}_${comeFrom}_${currentTimestamp}`

      // 调用服务端 API 发起通话
      const roomInfo = await loveRoomStart({
        target_id: String(targetId),
        count: 1,
        mode: loveRoomStart.MODE.VIDEO,
        action: loveRoomStart.ACTION.START,
        timestamp: currentTimestamp,
        trace_id: currentTraceId,
        come_from: comeFrom,
        gender: GENDER.FEMALE,
        pop_id: popId,
      }, {
        hideLoading: true,
      })

      // 验证RTC配置
      const rtcServer = roomInfo?.member?.rtc_server
      if (!rtcServer) {
        throw new Error('RTC server config not found in room info')
      }

      // 加入 Agora 频道
      await agoraManager.joinChannel({
        channelId: rtcServer.channel_id,
        token: rtcServer.access_token,
        uid: currentMemberId,
      })

      return roomInfo
    } catch (error) {
      console.error('[startCallAndJoinChannel] Failed:', error)
      throw error
    }
  }

  /**
   * Step 3: 注册USER_JOINED事件监听
   * @param {Object} params - 参数
   */
  function setupUserJoinedListener({ targetMemberId, roomInfo, currentMemberId, isTrial, live_id }) {
    isCallActive = true

    userJoinedHandler = ({ uid }) => {
      // 只处理目标用户的加入事件
      if (String(uid) !== String(targetMemberId)) {
        return
      }

      // 检查通话是否还在进行中
      if (!isCallActive) {
        return
      }

      console.log('[useVideoCall] Remote user joined:', uid)
      // 更新状态为已连接 ⚠️先更新状态再关闭弹窗，因为在弹窗关闭回调里会进行清理工作，此时要判断通话状态
      videoCallStore.connectCall()
      // 关闭等待弹窗
      closeVideoCall()

      // 上报通话接通埋点
      track('call_1v1_status_change_video_room', {
        live_id,
        status: 'connected',
        is_trial: isTrial,
      })

      // 跳转到视频通话页面
      router.push({
        name: 'video',
        params: { roomId: roomInfo.room_id },
        state: {
          comeFrom,
          liveId: live_id,
          mode: loveRoomStart.MODE.VIDEO,
          targetMember: Object.values(roomInfo.live_members).find(m => String(m.member_id) !== String(currentMemberId)),
          roomInfo,
          receiverMember: {
            member_id: targetMemberId,
          },
          senderMember: {
            member_id: currentMemberId,
          },
        },
      })

      // 解除监听
      agoraEventBus.off(AGORA_EVENTS.USER_JOINED, userJoinedHandler)
      userJoinedHandler = null
      isCallActive = false
    }

    // 注册监听
    agoraEventBus.on(AGORA_EVENTS.USER_JOINED, userJoinedHandler)
  }

  /**
   * Step 4: 显示等待弹窗并处理结果
   * @param {Object} params - 参数
   * @returns {Promise<void>}
   */
  async function showWaitingDialogAndHandleResult({ member, videoCostInfo, roomInfo, isTrial, live_id }) {
    const result = await showVideoCall({
      userInfo: {
        id: member.id,
        memberId: member.member_id,
        nickname: member.nickname,
        avatar: member.avatar,
        nationFlag: member.national_flag,
        nationName: member.nation,
      },
      free: videoCostInfo.showFree,
      price: roomInfo.video_cost,
      cardType: videoCostInfo.cardType,
      autoCloseSeconds: waitingTimeout,
      onClose: () => {
        if (userJoinedHandler) {
          agoraEventBus.off(AGORA_EVENTS.USER_JOINED, userJoinedHandler)
          userJoinedHandler = null
        }
        isCallActive = false
        isProcessing.value = false
        // 如果通话未连接，清理 Agora 资源
        if (!videoCallStore.isConnected) {
          cleanupAgoraResources()
        }
      }
    })

    // 处理用户操作
    if (result === 'hangup') {
      // 用户主动挂断
      track('call_1v1_status_change_video_room', {
        live_id,
        status: 'ended',
        duration: 0,
        end_reason: 'user_hangup',
        is_trial: isTrial,
      })
      await cancelCallApi(loveRoomStart.ACTION_TYPE.DEFAULT, currentTraceId, currentTimestamp)

      cancelCall()

      onHangup?.()
    }
    if (result === 'timeout') {
      // 超时未接听
      track('call_1v1_status_change_video_room', {
        live_id,
        status: 'ended',
        duration: 0,
        end_reason: 'timeout',
        is_trial: isTrial,
      })

      await cancelCallApi(loveRoomStart.ACTION_TYPE.TIMEOUT, currentTraceId, currentTimestamp)
      showToast(t('videoCall.timeout'))

      cancelCall()

      onTimeout?.()
    }
  }

  /**
   * 发起视频通话（重构版：职责拆分，更清晰的错误处理）
   * @param {Object} params - 通话参数
   * @param {string|number} params.targetMemberId - 目标用户 ID
   * @param {string|number} params.targetId - 目标用户 ID
   * @param {object} params.anchorStatus - 目标用户在线状态（用于埋点），可选
   * @param {Object} params.userRelation - 用户关系对象
   */
  async function initiateVideoCall({
    targetId,
    targetMemberId,
    anchorStatus,
    userRelation,
  }) {

    // 防止重复调用
    if (isProcessing.value) {
      console.warn('Video call is already in progress')
      return
    }
    isProcessing.value = true

    // 先重置通话状态，但只在非IDLE状态时重置，避免触发不必要的清理
    if (!videoCallStore.isIdle) {
      videoCallStore.resetCall()
    }

    const currentMemberId = userStore.getMemberId()
    showLoadingToast(t('common.loading'))

    // 计算价格展示
    const videoCostInfo = getVideoCostInfo({
      memberAsset: userStore.memberAsset,
      anchorStatus,
      price: anchorStatus?.price,
    })

    // Step 1: 检查用户关系
    if (!checkUserRelation(userRelation)) {
      return
    }

    // Step 2: 创建本地轨道
    const tracksCreated = await createLocalTracks()
    if (!tracksCreated) {
      return
    }

    // Step 3: 调用API并加入频道
    let roomInfo
    try {
      roomInfo = await startCallAndJoinChannel({
        targetId,
        targetMemberId,
        currentMemberId,
      })
    } catch (error) {
      cancelCall()

      // 显示错误信息
      handleVideoCallError({
        code: error.code,
        message: error.error || error.message,
        data: error.data || {},
      })

      trackVideoCallInitiate({
        targetMemberId,
        anchorStatus,
        actionResult: error.data?.action || 'other_error',
        cardType: videoCostInfo.cardType,
      })

      onFailure?.({
        reason: VIDEO_CALL_FAILURE_REASON.API_ERROR,
        message: error.error || error.message,
        code: error.code,
        error,
      })
      return
    }

    const { free_duration, live_id, live_members } = roomInfo
    const isTrial = free_duration > 0

    // Step 4: 注册USER_JOINED事件监听
    setupUserJoinedListener({
      targetMemberId,
      roomInfo,
      currentMemberId,
      isTrial,
      live_id,
    })

    // Step 5: 更新状态为呼叫中
    videoCallStore.startCall({
      ...roomInfo,
      traceId: currentTraceId,
    })

    // Step 6: 上报发起成功埋点
    trackVideoCallInitiate({
      targetMemberId,
      anchorStatus,
      actionResult: 'call_initiate',
      cardType: videoCostInfo.cardType,
    })

    // Step 7: 显示等待弹窗并处理结果
    showWaitingDialogAndHandleResult({
      member: live_members[targetMemberId],
      videoCostInfo,
      roomInfo,
      isTrial,
      live_id,
    })
  }

  /**
   * 取消通话（主动清理）
   */
  async function cancelCall() {
    videoCallStore.resetCall()
    await cleanupCallResources()
  }

  // 组件卸载时自动清理
  onBeforeUnmount(async () => {
    //  如果通话已连接，说明已经跳转到 VideoCallPage，不需要清理
    if (videoCallStore.isConnected) {
      return
    }

    // 如果有正在进行的通话（等待中），标记为异常结束
    if (videoCallStore.isCalling) {
      await cancelCallApi(loveRoomStart.ACTION_TYPE.ABNORMAL_END, currentTraceId, currentTimestamp)
    }

    if (isProcessing.value) {
      await cancelCall()
    }
  })

  return {
    // State
    isProcessing,

    // Methods
    initiateVideoCall,
    cancelCall,
  }
}
